import type { NitroFetchOptions } from '@/node_modules/nitropack'
import type { FetchContext } from 'ofetch'
import type { Pinia } from 'pinia'
import { objectToCamel } from 'ts-case-convert'
import { generateApiException } from '~/utils/error.helper'
import { ExceptionApiAccessTokenExpired } from '~/data/exceptions/ExceptionApiAccessTokenExpired'
import { sendServerError } from '~/utils/loki.helper'
import { ExceptionApiGeneral } from '~/data/exceptions/ExceptionApiGeneral'

class FetchService {
  private headerInfo: {
    osName: string
  }

  private readonly $pinia: Pinia

  constructor($pinia: Pinia, headerInfo: { osName: string }) {
    this.$pinia = $pinia
    this.headerInfo = headerInfo
  }

  get baseHeaders(): object {
    const authStore = useAuthStore(this.$pinia)
    let authToken = ''
    const blueGreen = useRequestHeaderStore(this.$pinia).blueGreen

    if (authStore.isLogin) {
      authToken = `Bearer ${authStore.accessToken}`
    }

    return {
      [REQUEST_HEADER_NAME.PLATFORM]: PLATFORM.WEB.LONG,
      // todo cgnt_id 필요?
      // [REQUEST_HEADER_NAME.X_IDUS_LOG_TOKEN]: authStore.cgntId,
      [REQUEST_HEADER_NAME.AUTHORIZATION]: authToken,
      // [REQUEST_HEADER_NAME.BLUE_GREEN]: blueGreen ?? 'blue',
    }
  }

  private onResponse(ctx: FetchContext) {
    // todo response handling, case처리?
  }

  private onResponseError(ctx: FetchContext) {
    // todo error handling
    const exception = generateApiException(ctx.response)

    if (exception instanceof ExceptionApiAccessTokenExpired) {
      navigateTo(URL_MAP.LOGOUT)
      return
    }

    if (exception instanceof ExceptionApiGeneral) {
      sendServerError(`${exception.message}`, 'FetchService')
    }

    throw exception
  }

  private async fetch<R>(
    url: string,
    options?: NitroFetchOptions<string>,
  ): Promise<R> {
    const nuxtApp = useNuxtApp()

    const headers = {
      ...this.baseHeaders,
      ...options?.headers,
    }
    delete options?.headers

    const response = await $fetch<R>(url, {
      headers,
      onResponse: (ctx) => {
        if (ctx.response?.status == 200) {
          // todo 여기서 처리하는게 맞을까?
          ctx.response._data = objectToCamel(ctx.response._data)
          nuxtApp.runWithContext(() => this.onResponse(ctx))
        }
      },
      onResponseError: this.onResponseError,
      retry: false,
      ...options,
    })

    // todo robots설정 이후 type에러가 발생하는데 원인 확인 필요
    return response as R
  }

  get<T extends Record<string, any>, R = any>(
    url: string,
    params?: T,
    options?: NitroFetchOptions<string, 'get'>,
  ): Promise<R> {
    return this.fetch<R>(url, { method: 'get', query: params, ...options })
  }

  post<T extends Record<string, any>, R = any>(
    url: string,
    data?: T,
    options?: NitroFetchOptions<string, 'post'>,
  ): Promise<R> {
    return this.fetch<R>(url, { method: 'post', body: data, ...options })
  }

  put<T extends Record<string, any>, R = any>(
    url: string,
    data?: T,
    options?: NitroFetchOptions<string, 'put'>,
  ): Promise<R> {
    return this.fetch<R>(url, { method: 'put', body: data, ...options })
  }

  patch<T extends Record<string, any>, R = any>(
    url: string,
    data?: T,
    options?: NitroFetchOptions<string, 'patch'>,
  ): Promise<R> {
    return this.fetch<R>(url, { method: 'PATCH', body: data, ...options })
  }

  delete<R = any>(
    url: string,
    options?: NitroFetchOptions<string, 'delete'>,
  ): Promise<R> {
    return this.fetch<R>(url, { method: 'delete', ...options })
  }
}

export default FetchService
